import { supabase } from "@fieldbook/common/supabase"
import { queryOptions } from "@tanstack/react-query"
import { powerSyncDb } from "@/lib/powersync/db"
import { getTrack, getTrackPoints } from "@/lib/tracksDb"
import {
  getLocalTrackGeoJson,
  getSyncedTrackGeoJson,
  type SyncedTrackRow,
  type TrackProperties,
} from "./geoJson"

const SYNCED_TRACK_QUERY = `
  SELECT id, created_by, geojson, started_at, ended_at, length_m, point_count
  FROM tracks
  WHERE id = ?
`

// How often an open recording's detail picks up new points.
const ACTIVE_TRACK_REFETCH_MS = 5_000

/**
 * One track as a LineString Feature, wherever it currently lives — the synced
 * row if PowerSync has one, otherwise the IndexedDB copy (a recording in
 * progress, or one saved moments ago and not yet published). Null when there
 * is no such track, or it is a local track another account recorded on this
 * device. `displayOnMap` is left unset: it's a device preference callers read
 * from useTrackDisplayPrefs.
 */
export const fetchTrackDetail = async (
  trackId: string,
): Promise<GeoJSON.Feature<GeoJSON.LineString, TrackProperties> | null> => {
  const syncedRow = await powerSyncDb.getOptional<SyncedTrackRow>(
    SYNCED_TRACK_QUERY,
    [trackId],
  )
  if (syncedRow) return getSyncedTrackGeoJson(syncedRow, false)

  const track = await getTrack(trackId)
  if (!track) return null

  const [
    points,
    {
      data: { session },
    },
  ] = await Promise.all([getTrackPoints(trackId), supabase.auth.getSession()])
  return getLocalTrackGeoJson(track, points, session?.user.id)
}

/**
 * Keyed under ["track", trackId] so the invalidations that already follow a
 * track through saving and publishing (useActiveTrack, publishTracks) refresh
 * it too.
 */
export const trackDetailQueryOptions = (trackId: string) =>
  queryOptions({
    queryKey: ["track", trackId, "detail"],
    queryFn: () => fetchTrackDetail(trackId),
    meta: { persisted: false },
    refetchInterval: (query) =>
      query.state.data && query.state.data.properties.status !== "completed"
        ? ACTIVE_TRACK_REFETCH_MS
        : false,
  })
